import { useState, useEffect } from 'react';

import moment from 'moment';
import { useForm, FormProvider } from 'react-hook-form-v7';
import { DATE_FORMATS } from '../../../../utils/Application_Constants';
import useGenerateInvoices from '../../../hooks/useGenerateInvoices';
import { useInvoicingSelectionContext } from '../../../Contexts/InvoicingSelectionContext';
import formatDate from '../../../../utils/formatDate';
import InvoiceConfirmationModal from '../InvoiceConfirmationModal';

const InvoiceInitiationConfirmationModal = ({
    open,
    onClose,
    action,
    doNotSendToClient,
    setDoNotSendToClient,
    setSuccessState,
    refetch,
}) => {
    const initializeFormData = () => ({
        postingDate: moment(Date.now()),
        invoiceDate: moment(Date.now()),
        doNotSendToClient: doNotSendToClient ?? false,
    });
    const [submissionError, setSubmissionError] = useState({ message: '', errors: [] });

    const { selectedItemsCount, getSelectedItems, deselectAllItems } =
        useInvoicingSelectionContext();

    const formMethods = useForm({
        defaultValues: initializeFormData(),
        mode: 'onBlur',
    });
    const watchDoNotSendToClient = formMethods.watch('doNotSendToClient');

    useEffect(() => {
        if (watchDoNotSendToClient !== doNotSendToClient) {
            setDoNotSendToClient(watchDoNotSendToClient);
        }
    }, [watchDoNotSendToClient, doNotSendToClient, setDoNotSendToClient]);

    const handleClose = () => {
        setSubmissionError({ message: '', errors: [] });
        formMethods.reset();
        onClose();
    };

    const { mutate: generateInvoices, isLoading } = useGenerateInvoices({
        onMutate: () => {
            setSuccessState({ showAlert: false, count: 0, action: '' });
            setSubmissionError({ message: '', errors: [] });
        },
        onSuccess: () => {
            setSuccessState({ showAlert: true, count: selectedItemsCount, action });
            deselectAllItems();
            onClose();
            refetch();
        },
        onError: (error) => {
            if (error?.errorId === 400 && error.message) {
                setSubmissionError({ message: error.message, errors: error.errors });
            } else {
                setSubmissionError({
                    message: `A system error prevented sending your selected items. Nothing was ${action}d. Please try again.`,
                    errors: [],
                });
            }
        },
    });
    const onSubmit = (formData) => {
        const dataToSubmit = {
            ...formData,
            invoiceDate: formatDate(formData.invoiceDate.toDate(), {
                format: DATE_FORMATS.ISO_8601,
            }),
            postingDate: formatDate(formData.postingDate.toDate(), {
                format: DATE_FORMATS.ISO_8601,
            }),
            toInvoice: getSelectedItems(),
            preInvoice: action === 'pre-invoice',
        };

        generateInvoices(dataToSubmit);
    };

    return (
        <FormProvider {...formMethods}>
            <InvoiceConfirmationModal
                open={open}
                onClose={handleClose}
                title={`Confirm ${action === 'pre-invoice' ? 'Pre-' : ''}Invoices`}
                bodyMessage={`You've selected ${selectedItemsCount} unbilled item(s). Do you wish to process them?`}
                showInvoiceForm
                onSubmit={formMethods.handleSubmit(onSubmit)}
                error={submissionError}
                isLoading={isLoading}
                actionButtonLabel={`Create ${action === 'pre-invoice' ? 'Pre-' : ''}Invoices`}
            />
        </FormProvider>
    );
};

export default InvoiceInitiationConfirmationModal;
